import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { PawPrint, Briefcase, User, ArrowRight, Loader2 } from 'lucide-react';
import api from '../lib/api';

export default function ProfileSelection() {
    const navigate = useNavigate();
    const { user, updateUser, updateToken } = useAuth();
    const [selectedType, setSelectedType] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleSelect = async (type) => {
        setSelectedType(type);
        setLoading(true);

        try {
            if (type === 'tutor') {
                // TUTOR: Ativar imediatamente e ir para dashboard
                const response = await api.post('/auth/activate-tutor');
                const { user: freshUser, token: newToken } = response.data;
                
                // Atualizar token e contexto
                if (newToken) updateToken(newToken);
                updateUser(freshUser);
                
                toast.success('Bem-vindo ao RecepPet!');
                navigate('/tutor', { replace: true });
            } else {
                // PRESTADOR: Ir para registro, pulando identificação pessoal
                toast.info('Complete seu cadastro de prestador');
                navigate('/register', { 
                    state: { 
                        fromGoogle: true, 
                        googleUser: {
                            name: user?.name,
                            email: user?.email,
                            profile_photo: user?.profile_photo
                        }
                    },
                    replace: true 
                });
            }
        } catch (error) {
            console.error('Error selecting profile:', error);
            toast.error('Erro ao salvar. Tente novamente.');
            setLoading(false);
            setSelectedType(null);
        }
    };

    return (
        <div className="min-h-screen hero-gradient flex items-center justify-center p-4" data-testid="profile-selection-page">
            <Card className="w-full max-w-lg shadow-xl border-0">
                <CardHeader className="text-center pb-2">
                    <div className="w-16 h-16 bg-gradient-to-r from-[#4A90B2] to-[#7AB89C] rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <User className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold font-['Nunito']">
                        Bem-vindo ao RecepPet!
                    </CardTitle>
                    <CardDescription className="text-base">
                        Como você deseja usar a plataforma?
                    </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 pt-4">
                    {/* Opção Tutor */}
                    <button
                        onClick={() => handleSelect('tutor')}
                        disabled={loading}
                        data-testid="select-tutor-btn"
                        className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                            selectedType === 'tutor' 
                                ? 'border-[#4A90B2] bg-[#4A90B2]/10' 
                                : 'border-slate-200 hover:border-[#4A90B2]/50 hover:bg-slate-50'
                        }`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                                selectedType === 'tutor' ? 'bg-[#4A90B2]' : 'bg-slate-100'
                            }`}>
                                <PawPrint className={`w-7 h-7 ${
                                    selectedType === 'tutor' ? 'text-white' : 'text-[#4A90B2]'
                                }`} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-lg text-slate-800">Sou Tutor</h3>
                                <p className="text-sm text-slate-500">
                                    Quero encontrar cuidadores para meu pet
                                </p>
                            </div>
                            {loading && selectedType === 'tutor' ? (
                                <Loader2 className="w-5 h-5 animate-spin text-[#4A90B2]" />
                            ) : (
                                <ArrowRight className="w-5 h-5 text-slate-400" />
                            )}
                        </div>
                    </button>

                    {/* Opção Prestador */}
                    <button
                        onClick={() => handleSelect('provider')}
                        disabled={loading}
                        data-testid="select-provider-btn"
                        className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
                            selectedType === 'provider' 
                                ? 'border-[#7AB89C] bg-[#7AB89C]/10' 
                                : 'border-slate-200 hover:border-[#7AB89C]/50 hover:bg-slate-50'
                        }`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                                selectedType === 'provider' ? 'bg-[#7AB89C]' : 'bg-slate-100'
                            }`}>
                                <Briefcase className={`w-7 h-7 ${
                                    selectedType === 'provider' ? 'text-white' : 'text-[#7AB89C]'
                                }`} />
                            </div>
                            <div className="flex-1">
                                <h3 className="font-bold text-lg text-slate-800">Sou Prestador</h3>
                                <p className="text-sm text-slate-500">
                                    Quero oferecer serviços de cuidado pet
                                </p>
                            </div>
                            {loading && selectedType === 'provider' ? (
                                <Loader2 className="w-5 h-5 animate-spin text-[#7AB89C]" />
                            ) : (
                                <ArrowRight className="w-5 h-5 text-slate-400" />
                            )}
                        </div>
                    </button>

                    <div className="pt-4 text-center">
                        <p className="text-xs text-slate-400">
                            Você poderá adicionar outro perfil posteriormente nas configurações
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
